// An edit sheet over the page, as on the customer record: its title, the form, then Save and Cancel.
// useWrite is one write: its -ing label while it runs, the server's words when it is refused, and
// one idempotency key per attempt at the same thing (QA-058). When someone else changed the record
// since the sheet opened (a rowVersion conflict, F-53), "Get the latest" reads it again and the form
// follows it: what was typed stays, the rest takes the latest (useRebase).

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { client } from '../api.ts';
import { SaveButton, useSaveKey } from './SaveButton.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import { isStale, rebase } from './rebase.ts';
import './new-customer.css';
import '../history/history.css';

export type Built = { input: Record<string, unknown> } | { error: string };

export function Sheet({
  title,
  wide,
  onClose,
  children,
}: {
  title: string;
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`sheet ask edit-sheet${wide ? ' wide' : ''}`}
      aria-labelledby={titleId}
      onClose={onClose}
    >
      <h3 id={titleId}>{title}</h3>
      <div className="nc-form">{children}</div>
    </dialog>
  );
}

/** Keeps a form on the record it edits: when the record's version moves (read again), rebase onto it. */
export function useRebase<T extends object>(
  latest: T,
  version: number | undefined,
  setF: (u: (f: T) => T) => void,
) {
  const base = useRef(latest);
  useEffect(() => {
    const was = base.current;
    base.current = latest;
    if (was !== latest) setF((f) => rebase(was, f, latest));
    // the version says when the record was read again; `latest` is new on every render
  }, [version]);
}

export function useWrite(signature: string) {
  const save = useSaveKey(signature);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  /**
   * `orElse`: on a refusal, another call to make instead (null: none), e.g. a texts-off the mirror refuses
   * through customers.setConsent, kept by customers.recordOptOut (QA-615).
   */
  const run = async <T,>(
    action: string,
    built: Built,
    step?: string,
    orElse?: (e: unknown) => { action: string; input: unknown } | null,
  ): Promise<T | null> => {
    if ('error' in built) {
      setError(built.error);
      return null;
    }
    setBusy(true);
    setError(null);
    setStale(false);
    try {
      const r = await client.call<T>(action, built.input, {
        idempotencyKey: step ? `${save.key}:${step}` : save.key,
      });
      if (!step) save.renew();
      return r;
    } catch (e) {
      const alt = orElse?.(e);
      if (alt)
        try {
          const r = await client.call<T>(alt.action, alt.input, { idempotencyKey: `${save.key}:else` });
          if (!step) save.renew();
          return r;
        } catch (e2) {
          setError(refusal(e2));
          return null;
        }
      setError(refusal(e));
      setStale(isStale(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  /** "Get the latest": read the record again (the caller's), then say what happened to the form. */
  const latest = async (read: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await read();
      // the next save is a new attempt (a newer version): the refused one's key would only replay it
      save.renew();
      setStale(false);
      setError('This is the latest now. What you changed is kept: check it, then save again.');
    } catch (e) {
      setError(refusal(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run, key: save.key, stale, latest };
}

export function SheetButtons({
  w,
  label,
  busyLabel,
  disabled,
  onSave,
  onCancel,
  onLatest,
}: {
  w: {
    busy: boolean;
    error: string | null;
    stale?: boolean;
    latest?: (read: () => Promise<unknown>) => Promise<void>;
  };
  label: string;
  busyLabel: string;
  disabled?: boolean;
  onSave: () => void;
  onCancel: () => void;
  /** Read the record again, for "Get the latest" after someone else's change (F-53). */
  onLatest?: () => Promise<unknown>;
}) {
  return (
    <div className="row">
      <SaveButton busy={w.busy} busyLabel={busyLabel} error={w.error} disabled={disabled} onClick={onSave}>
        {label}
      </SaveButton>
      {w.stale && onLatest && w.latest && (
        <button className="btn soft" type="button" disabled={w.busy} onClick={() => void w.latest!(onLatest)}>
          Get the latest
        </button>
      )}
      <button className="btn quiet" type="button" onClick={onCancel} disabled={w.busy}>
        Cancel
      </button>
    </div>
  );
}
